import { Box, MenuItem, Select, CircularProgress } from "@mui/material";
import { useState, useEffect } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { clientApis } from "../../../Data/axios";

import styles from "./AttendenceHeader.module.css";

// Allowed client IDs
const ALLOWED_CLIENT_IDS = [
  'RPTPLCLNT1764834914',
  'RPTPLCLNT1748930136',
  'RPTPLCLNT1748236300'
];

export default function AttendenceHeader({ onClientChange, initialClientId }) {
  const [selection, setSelection] = useState(0);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [loadingClients, setLoadingClients] = useState(false);

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const response = await clientApis.getAll(1, 50);
        if (response && response.data && Array.isArray(response.data)) {
          // Filter clients to only include allowed client IDs
          const filteredClients = response.data.filter(client =>
            ALLOWED_CLIENT_IDS.includes(client.user_id)
          );
          setClients(filteredClients);

          // Use initialClientId if provided and valid, otherwise use first filtered client
          let initialSelection = initialClientId;
          if (!initialSelection || !ALLOWED_CLIENT_IDS.includes(initialSelection)) {
            initialSelection = filteredClients.length > 0 ? filteredClients[0].user_id : "";
          }

          setSelectedClient(initialSelection);
          if (onClientChange && initialSelection) {
            onClientChange(initialSelection);
          }
        } else {
          setClients([]);
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, [initialClientId, onClientChange]);

  const handleChange = (event, newValue) => {
    setSelection(newValue);
  };

  const handleClientChange = (event) => {
    const clientId = event.target.value;
    setSelectedClient(clientId);
    if (onClientChange) {
      onClientChange(clientId);
    }
  };

  // Get last 2 months
  const getLastTwoMonths = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

    return [
      { value: currentMonth, label: new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long' }), year: currentYear },
      { value: lastMonth, label: new Date(lastMonthYear, lastMonth).toLocaleString('default', { month: 'long' }), year: lastMonthYear }
    ];
  };

  const lastTwoMonths = getLastTwoMonths();

  return (
    <Box className={styles.top}>
      {/* Client Filter */}
      <Box
        sx={{
          width: "250px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingLeft: "10px",
        }}
        className={styles.box}
      >
        <p
          style={{
            marginRight: "10px",
            fontSize: "18px",
            color: "#333",
            fontWeight: 540,
          }}
        >
          Client
        </p>
        <Select
          value={selectedClient}
          onChange={handleClientChange}
          displayEmpty
          disabled={loadingClients}
          renderValue={(selected) => {
            if (!selected) {
              return "Select Client";
            }
            const client = clients.find(c => c.user_id === selected);
            return client ? client.company_name : selected;
          }}
          sx={{
            width: "145px",
            margin: ".4rem",
            padding: "1.5px",
            border: "none",
            color: "#000 ",
            bgcolor: "#eff1f1",
            "& .MuiSelect-select": {
              padding: 0,
            },
          }}
          IconComponent={(props) => (
            <ExpandMoreIcon {...props} style={{ color: "black" }} />
          )}
        >
          <MenuItem value="">Select Client</MenuItem>
          {loadingClients ? (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Loading...
            </MenuItem>
          ) : (
            clients.map((client) => (
              <MenuItem key={client.user_id} value={client.user_id}>
                {client.company_name}
              </MenuItem>
            ))
          )}
        </Select>
      </Box>

      <Box
        sx={{
          width: "225px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingLeft: "10px",
        }}
        className={styles.box}
      >
        <p
          style={{
            marginRight: "10px",
            fontSize: "18px",
            color: "#333",
            fontWeight: 540,
          }}
        >
          Employee
        </p>
        <Select
          value={selection}
          onChange={handleChange}
          displayEmpty
          renderValue={(selected) => {
            if (selected === 0) {
              return <MenuItem value={0}>All</MenuItem>; // Placeholder text
            }
            return selected;
          }}
          sx={{
            width: "145px",
            margin: ".4rem",
            padding: "1.5px",
            border: "none",
            color: "#000 ",
            bgcolor: "#eff1f1",
            "& .MuiSelect-select": {
              padding: 0,
            },
          }}
          IconComponent={(props) => (
            <ExpandMoreIcon {...props} style={{ color: "black" }} />
          )}
        >
          <MenuItem value={0}>All</MenuItem>
          <MenuItem value={1}>Open Tickets</MenuItem>
          <MenuItem value={2}>Pending Tickets</MenuItem>
          <MenuItem value={3}>Resolved Tickets</MenuItem>
          <MenuItem value={4}>Closed Tickets</MenuItem>
        </Select>
      </Box>
      <Box
        sx={{
          width: "230px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingLeft: "10px",
        }}
        className={styles.box}
      >
        <p
          style={{
            marginRight: "10px",
            fontSize: "18px",
            color: "#333",
            fontWeight: 540,
          }}
        >
          Department
        </p>
        <Select
          value={selection}
          onChange={handleChange}
          displayEmpty
          renderValue={(selected) => {
            if (selected === 0) {
              return <MenuItem value={0}>All</MenuItem>; // Placeholder text
            }
            return selected;
          }}
          sx={{
            width: "145px",
            margin: ".4rem",
            padding: "1.5px",
            border: "none",
            color: "#000 ",
            bgcolor: "#eff1f1",
            "& .MuiSelect-select": {
              padding: 0,
            },
          }}
          IconComponent={(props) => (
            <ExpandMoreIcon {...props} style={{ color: "black" }} />
          )}
        >
          <MenuItem value={0}>All</MenuItem>
          <MenuItem value={1}>Marketing</MenuItem>
          <MenuItem value={2}>Sales</MenuItem>
          <MenuItem value={3}>Human Resources</MenuItem>
          <MenuItem value={4}>Public Relations</MenuItem>
          <MenuItem value={4}>Research</MenuItem>
          <MenuItem value={4}>Finance</MenuItem>
        </Select>
      </Box>
      <Box
        sx={{
          width: "230px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingLeft: "10px",
        }}
        className={styles.box}
      >
        <p
          style={{
            marginRight: "10px",
            fontSize: "18px",
            color: "#333",
            fontWeight: 540,
          }}
        >
          Designation
        </p>
        <Select
          value={selection}
          onChange={handleChange}
          displayEmpty
          renderValue={(selected) => {
            if (selected === 0) {
              return <MenuItem value={0}>All</MenuItem>; // Placeholder text
            }
            return selected;
          }}
          sx={{
            width: "145px",
            margin: ".4rem",
            padding: "1.5px",
            border: "none",
            color: "#000 ",
            bgcolor: "#eff1f1",
            "& .MuiSelect-select": {
              padding: 0,
            },
          }}
          IconComponent={(props) => (
            <ExpandMoreIcon {...props} style={{ color: "black" }} />
          )}
        >
          <MenuItem value={0}>All</MenuItem>
          <MenuItem value={1}>Trainee</MenuItem>
          <MenuItem value={2}>Junior</MenuItem>
          <MenuItem value={3}>Senior</MenuItem>
          <MenuItem value={4}>Team Lead</MenuItem>
          <MenuItem value={4}>Project Manager </MenuItem>
        </Select>
      </Box>
      <Box
        sx={{
          width: "200px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingLeft: "10px",
        }}
        className={styles.box}
      >
        <p
          style={{
            marginRight: "10px",
            fontSize: "18px",
            color: "#333",
            fontWeight: 540,
          }}
        >
          Month
        </p>
        <Select
          value={selection}
          onChange={handleChange}
          displayEmpty
          renderValue={(selected) => {
            if (selected === 0) {
              return <MenuItem value={0}>{lastTwoMonths[0]?.label || "Month"}</MenuItem>;
            }
            const month = lastTwoMonths.find(m => m.value === selected);
            return month ? month.label : selected;
          }}
          sx={{
            width: "115px",
            margin: ".4rem",
            padding: "1.5px",
            border: "none",
            color: "#000 ",
            bgcolor: "#eff1f1",
            "& .MuiSelect-select": {
              padding: 0,
            },
          }}
          IconComponent={(props) => (
            <ExpandMoreIcon {...props} style={{ color: "black" }} />
          )}
        >
          {lastTwoMonths.map((month) => (
            <MenuItem key={`${month.year}-${month.value}`} value={month.value}>
              {month.label} {month.year}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box
        sx={{
          width: "180px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingLeft: "10px",
        }}
        className={styles.box}
      >
        <p
          style={{
            marginRight: "10px",
            fontSize: "18px",
            color: "#333",
            fontWeight: 540,
          }}
        >
          Year
        </p>
        <Select
          value={selection}
          onChange={handleChange}
          displayEmpty
          // Use renderValue to show placeholder text when value is empty
          renderValue={(selected) => {
            if (selected === 0) {
              return <MenuItem value={0}>{lastTwoMonths[0]?.year || "Year"}</MenuItem>;
            }
            const month = lastTwoMonths.find(m => m.value === selected);
            return month ? month.year.toString() : selected.toString();
          }}
          sx={{
            width: "75px",
            margin: ".4rem",
            padding: "1.5px",
            border: "none",
            color: "#000 ",
            bgcolor: "#eff1f1",
            "& .MuiSelect-select": {
              padding: 0,
            },
          }}
          IconComponent={(props) => (
            <ExpandMoreIcon {...props} style={{ color: "black" }} />
          )}
        >
          {Array.from(new Set(lastTwoMonths.map(m => m.year))).map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  );
}
